<script setup lang="ts">
import { debounce } from 'lodash-es';
import InputGroupAddon from 'primevue/inputgroupaddon';
import InputText from 'primevue/inputtext';
import { useField } from 'vee-validate';
import {
  computed,
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ShallowRef,
  shallowRef,
  watch,
} from 'vue';

import { useWangsVueConfig } from 'lib/plugins/WangsVue';
import mergePropsWithDefaults from 'lib/utils/mergePropsWithDefaults.util';

import { InputTextEmits, InputTextProps } from './InputText.vue.d';
import FieldWrapper from '../fieldwrapper/FieldWrapper.vue';
import { FieldValidation } from '../form/Form.vue.d';
import InputGroup from '../inputgroup/InputGroup.vue';
import { Nullable } from '../ts-helpers.d';
import ValidatorMessage from '../validatormessage/ValidatorMessage.vue';

const props = withDefaults(defineProps<InputTextProps>(), {
  type: 'text',
  validateOnBlur: false,
  autoUppercase: false,
  manualInvalidContainer: false,
  useProtocol: true,
  allowSpecialCharacters: true,
  useInputGroup: true,
});

const emit = defineEmits<InputTextEmits>();

const { defaultProps } = useWangsVueConfig(
  // eslint-disable-next-line no-nested-ternary
  props.type === 'email'
    ? 'InputEmail'
    : props.type === 'url'
      ? 'InputURL'
      : 'InputText',
);

const { locale } = useWangsVueConfig('InputText');

const config = mergePropsWithDefaults(props, defaultProps);

const InputGroupAddonPreset = inject<Record<string, any>>(
  'preset',
  {},
).inputgroupaddon;

const CHECK_AVAILABILITY_ERROR_MESSAGE = 'Checking availability...';

const field = reactive<FieldValidation<Nullable<string>>>({
  value: undefined,
});

const isSubmittingForm = inject<ShallowRef<boolean> | undefined>(
  'isSubmitting',
);

const invalidInput = computed(() => props.invalid || !!field.errorMessage);

const inputKey = shallowRef(0);
const temporaryValue = shallowRef<Nullable<string>>(
  props.value || props.modelValue,
); // Temporary store the input value for props.validateOnBlur

onMounted(() => {
  if (props.useValidator) {
    Object.assign(
      field,
      useField(props.fieldName ?? 'textInput', (value: string) => {
        return nextTick(() => setValidatorMessage(value?.trim())); // Waits props.invalid changed
      }),
    );

    if (props.value != null) assignFieldValue(props.value);
  }

  if (props.modelValue != null) field.value = props.modelValue?.trim();
  if (props.invalid && props.useValidator) field.validate();
});

onBeforeUnmount(() => {
  if (props.useValidator) {
    field.handleReset();
  }
});

const inputPlaceholder = computed(
  () =>
    props.placeholder ??
    locale.defaultPlaceholder?.replace(
      '{label}',
      props.label ? props.label?.toLowerCase() : '',
    ),
);

const existErrorMessage = computed(() => {
  return typeof props.validatorMessage === 'object' &&
    'exist' in props.validatorMessage
    ? props.validatorMessage?.exist
    : locale.alreadyExistInvalidText?.replace('{label}', props.label);
});

const setValidatorMessage = (value: string): boolean | string => {
  if (typeof props.validatorMessage === 'string' && props.invalid) {
    return props.validatorMessage;
  } else if (!props.allowSpecialCharacters) {
    return /[^A-Za-z0-9 ]/.test(value)
      ? locale.includeSpecialCharsInvalidText
      : true;
  } else if (typeof props.validatorMessage !== 'string') {
    const { invalidFormat, empty, exceed } = props.validatorMessage ?? {};
    if (!value && props.mandatory) {
      return empty ?? locale.emptyInvalidText.replace('{label}', props.label);
    } else if (value?.length > config.maxLength) {
      return (
        exceed ??
        locale.exceedMaxLengthInvalidText?.replace(
          '{maxLength}',
          config.maxLength.toString(),
        )
      );
    } else if (
      (props.existingValues ?? [])
        .filter((v) => v != props.value) // Existing value may contains initial value on Edit Form. Need to filter.
        ?.includes(value)
    ) {
      return (
        props.validatorMessage?.exist ??
        locale.alreadyExistInvalidText?.replace('{label}', props.label)
      );
    } else if (value && props.type === 'email') {
      return validateEmail(
        value,
        invalidFormat ?? locale.emailFormatInvalidText,
      );
    } else if (value && props.type === 'url') {
      return validateURL(value, invalidFormat ?? locale.urlFormatInvalidText);
    }
  }

  if (props.checkAvailability) return handleAvailabilityValidation();

  return true;
};

const validateURL = (
  value: string,
  invalidFormat?: string,
): boolean | string => {
  const urlRegExp = new RegExp(
    `^(https?:\\/\\/)${props.useProtocol ? '' : '?'}` + // Protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // Domain name and extension
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR IP (v4) address
      '(\\:\\d+)?' + // Port
      "(\\/[-a-z\\d%_.~+:@!$&'()*+,;=]*)*" + // Path (Updated to allow special characters)
      "(\\?[;&a-z\\d%_.~+=:@!$&'()*+,;=-]*)?" + // Query string (Allowing special characters)
      "(\\#[-a-z\\d_:@!$&'()*+,;=]*)?$", // Fragment (Allowing special characters)
    'i',
  );

  return invalidFormat == null || urlRegExp.test(value) ? true : invalidFormat;
};

const validateEmail = (
  value: string,
  invalidFormat?: string,
): boolean | string => {
  const emailRegexp = /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/;
  return invalidFormat == null || emailRegexp.test(value)
    ? !props.checkAvailability ||
        (!!props.checkAvailability && handleAvailabilityValidation())
    : invalidFormat;
};

const onBlurEvent = (): void => {
  if (props.validateOnBlur) field.value = temporaryValue.value;
  emit('blur', temporaryValue.value);
};

const handleKeypress = (event: KeyboardEvent): void => {
  const isMaxLengthReached =
    config.maxLength && config.maxLength === temporaryValue.value?.length;

  const isInvalidCharacter =
    props.allowedCharacters && !props.allowedCharacters.test(event.key);

  if (
    isInvalidCharacter ||
    (isMaxLengthReached &&
      (props.preventInputOnMaxLength || props.blurOnReachMaxLength))
  ) {
    event.preventDefault();

    if (props.blurOnReachMaxLength && isMaxLengthReached) {
      inputKey.value++;
    }
  }
};

const handlePaste = (event: ClipboardEvent): void => {
  const pastedData = event.clipboardData?.getData('text');

  const isMaxLengthReached =
    (config.maxLength && config.maxLength === temporaryValue.value?.length) ||
    temporaryValue.value?.length + pastedData?.length > config.maxLength ||
    pastedData?.length > config.maxLength;

  if (
    (props.allowedCharacters && !props.allowedCharacters.test(pastedData)) ||
    (isMaxLengthReached &&
      (props.preventInputOnMaxLength || props.blurOnReachMaxLength))
  ) {
    event.preventDefault();

    if (props.blurOnReachMaxLength && isMaxLengthReached) {
      inputKey.value++;
    }
  }
};

const onUpdateModelValue = (e?: string): void => {
  if (e === '') e = undefined;

  if (props.autoUppercase && e) {
    e = e.replace(/[a-z]/g, (char) => char.toUpperCase());
  }

  temporaryValue.value = e;

  if ((!props.validateOnBlur && props.useValidator) || !props.useValidator) {
    field.value = temporaryValue.value;
  }

  emit('update:modelValue', temporaryValue.value);
};

const onInput = (event: Event): void => {
  emit('input', (event.target as HTMLInputElement).value || undefined);
};

const handleAvailabilityValidation = (): string | true => {
  if (!isSubmittingForm?.value) {
    checkForAvailability();
    return CHECK_AVAILABILITY_ERROR_MESSAGE; // To prevent submit while checking availability
  } else if (
    field.errorMessage === existErrorMessage.value ||
    field.errorMessage === CHECK_AVAILABILITY_ERROR_MESSAGE
  ) {
    /**
     * Prevents form submission if an error occurs due to non-existence or if the check availability process is still in progress.
     * Returns the error to prevent the form from being submitted.
     */
    return field.errorMessage;
  }

  return true;
};

const checkForAvailability = debounce(async (): Promise<void> => {
  if (field.value != props.value) {
    if (field.value?.length) {
      const available = await props.checkAvailability(field.value);

      if (!available) {
        return field.setErrors(existErrorMessage.value);
      }
    }
  }

  if (field.errorMessage === CHECK_AVAILABILITY_ERROR_MESSAGE) {
    field.setErrors(''); // To make the field valid when name is available
  }
}, 300);

const assignFieldValue = (value?: string): void => {
  if (props.useValidator) field.setValue(value?.trim(), false);
  else field.value = value?.trim();
};

/**
 * For async data, watch the changes of modelValue once.
 */
watch(
  () => props.value,
  (newValue) => {
    assignFieldValue(newValue);
    temporaryValue.value = newValue?.trim();
  },
  { once: true },
);

watch(
  () => props.modelValue,
  (value) => {
    temporaryValue.value = value?.trim();
    if (!props.validateOnBlur) field.value = temporaryValue.value;
  },
);

watch(
  () => props.invalid,
  () => {
    field.validate?.();
  },
);
</script>

<template>
  <FieldWrapper
    :info="fieldInfo"
    :label="props.label"
    :label-class="props.labelClass"
    :mandatory="props.mandatory"
  >
    <InputGroup
      :class="
        props.inputContainerClass ?? [
          'grid ',
          {
            'grid-cols-[max-content,1fr,max-content]':
              $slots['addon-left'] && $slots['addon-right'],
            'grid-cols-[1fr,max-content]':
              !$slots['addon-left'] && $slots['addon-right'],
            'grid-cols-[max-content,1fr]':
              $slots['addon-left'] && !$slots['addon-right'],
          },
        ]
      "
      :disabled="props.disabled"
      :invalid="invalidInput && !props.manualInvalidContainer"
      :use-input-group="useInputGroup"
    >
      <InputGroupAddon v-if="$slots['addon-left']" :disabled="props.disabled">
        <slot name="addon-left" />
      </InputGroupAddon>

      <InputText
        :id="inputId"
        :key="inputKey"
        :class="inputClass"
        :disabled="disabled"
        :invalid="invalidInput"
        :model-value="field.value"
        :placeholder="inputPlaceholder"
        @blur="onBlurEvent"
        @input="onInput"
        @keypress="handleKeypress"
        @paste="handlePaste"
        @update:model-value="onUpdateModelValue"
      />

      <InputGroupAddon
        v-if="$slots['addon-right']"
        :pt="{
          root: InputGroupAddonPreset.root({
            props: {
              class: [
                {
                  '!text-general-200': props.disabled,
                },
              ],
            },
          }),
        }"
      >
        <slot name="addon-right" />
      </InputGroupAddon>
    </InputGroup>

    <span
      v-if="field.errorMessage == CHECK_AVAILABILITY_ERROR_MESSAGE"
      v-bind="$preset.validatormessage.root"
      class="text-general-800"
      data-wv-section="checking-availability"
    >
      Checking availability...
    </span>

    <ValidatorMessage
      v-else
      :class="validatorMessageClass"
      :message="field.errorMessage"
    />
  </FieldWrapper>
</template>
